import { View, Dialog } from 'web-mojo';
import { LocationClient } from 'web-mojo/map';
import { RouteTemplate } from '@portal/models/RouteTemplate.js';
import StopConfigDrawer from '@portal/routes/views/StopConfigDrawer.js';

export default class RouteTemplateEditorView extends View {
    constructor(options = {}) {
        super({
            className: 'route-template-editor',
            ...options
        });
        this.template_model = null;
        this.stops = [];
        this.operators = [];
        this.isNew = !options.model?.get?.('id');
        this._templateId = options.model?.get?.('id') || null;
        this._dragSrcIndex = null;
        this.saving = false;
        this._depotClient = new LocationClient();
        this._depotSuggestions = [];
        this.mapPanel = null;
    }

    async onInit() {
        const [, opResponse] = await Promise.all([
            this._loadTemplate(this._templateId),
            app.api.get('/api/routes/operator', { group: app.activeGroup.id, status: 'active', size: 200 }),
        ]);
        if (opResponse.success && opResponse.data.status) {
            this.operators = opResponse.data.data || [];
        }

        this.mapPanel = new TemplatePlanningMap({
            containerId: 'planning-map',
            stops: this.stops,
            depot: this._getDepot(),
        });
        this.addChild(this.mapPanel);
    }

    _getDepot() {
        const t = this.template_model;
        const lat = t?.get('default_start_latitude');
        const lng = t?.get('default_start_longitude');
        const address = t?.get('default_start_address');
        return (lat && lng) ? { lat, lng, address } : null;
    }

    async onAfterRender() {
        await super.onAfterRender();
        this._bindDrag();
        this._bindDepotInput();
    }

    _bindDepotInput() {
        const input = this.element?.querySelector('[name=default_start_address]');
        if (!input) return;

        let debounce;
        input.addEventListener('input', () => {
            clearTimeout(debounce);
            const q = input.value.trim();
            if (q.length < 3) {
                this._depotSuggestions = [];
                this._updateDepotSuggestions();
                return;
            }
            debounce = setTimeout(async () => {
                const result = await this._depotClient.autocomplete(q);
                this._depotSuggestions = result?.data || result?.suggestions || result?.predictions || [];
                this._updateDepotSuggestions();
            }, 220);
        });

        input.addEventListener('blur', () => {
            setTimeout(() => {
                this._depotSuggestions = [];
                this._updateDepotSuggestions();
            }, 200);
        });
    }

    _updateDepotSuggestions() {
        const box = this.element?.querySelector('[data-container="depot-suggestions"]');
        if (!box) return;
        if (!this._depotSuggestions.length) { box.innerHTML = ''; return; }
        box.innerHTML = this._depotSuggestions.map((s, i) => `
            <div class="list-group-item list-group-item-action py-2 px-3 small"
                style="cursor:pointer;" data-action="pick-depot-suggestion" data-index="${i}">
                <i class="bi bi-geo-alt text-muted me-2"></i>${s.description || s.formatted_address || s.text}
            </div>
        `).join('');
    }

    async onActionPickDepotSuggestion(event, el) {
        const index = parseInt(el.getAttribute('data-index'));
        const suggestion = this._depotSuggestions[index];
        if (!suggestion) return;

        this._depotSuggestions = [];
        this._updateDepotSuggestions();

        const placeId = suggestion.place_id || suggestion.id;
        const details = await this._depotClient.placeDetails({
            place_id: placeId,
            session_token: this._depotClient.sessionToken,
        });

        const place = details?.address || details;
        const lat = place?.latitude;
        const lng = place?.longitude;
        const address = place?.formatted_address || suggestion.description;

        const addressEl = this.element.querySelector('[name=default_start_address]');
        if (addressEl) addressEl.value = address || '';

        const latEl = this.element.querySelector('[name=default_start_latitude]');
        const lngEl = this.element.querySelector('[name=default_start_longitude]');
        if (latEl) latEl.value = lat != null ? lat : '';
        if (lngEl) lngEl.value = lng != null ? lng : '';

        if (lat != null && lng != null) {
            this.mapPanel?.updateDepot({ lat, lng, address });
        }
    }

    async _loadTemplate(id) {
        if (!id) {
            this.template_model = new RouteTemplate();
            this.stops = [];
            return;
        }

        const response = await app.api.get(`/api/routes/template/${id}`);
        if (!response.success || !response.data.status) {
            app.toast.error('Failed to load template');
            return;
        }
        this.template_model = new RouteTemplate(response.data.data);

        const stopsResponse = await app.api.get('/api/routes/template_stop', {
            template: id,
            sort: 'sequence_position',
            size: 200,
        });
        if (stopsResponse.success && stopsResponse.data.status) {
            this.stops = stopsResponse.data.data || [];
        }
    }

    async _loadStops() {
        const id = this.template_model?.get('id');
        if (!id) return;
        const response = await app.api.get('/api/routes/template_stop', {
            template: id,
            sort: 'sequence_position',
            size: 200,
        });
        if (response.success && response.data.status) {
            this.stops = response.data.data || [];
        }
    }

    _refreshMap() {
        this.mapPanel?.updateStops(this.stops, this._getDepot());
    }

    async onActionSave() {
        if (this.saving) return;
        const name = this.element.querySelector('[name=name]').value.trim();
        if (!name) {
            app.toast.warning('Template name is required');
            return;
        }

        const depotLat = parseFloat(this.element.querySelector('[name=default_start_latitude]')?.value) || null;
        const depotLng = parseFloat(this.element.querySelector('[name=default_start_longitude]')?.value) || null;

        const payload = {
            name,
            route_type: this.element.querySelector('[name=route_type]').value.trim(),
            recurrence_pattern: this.element.querySelector('[name=recurrence_pattern]').value.trim(),
            default_start_address: this.element.querySelector('[name=default_start_address]').value.trim(),
            default_start_latitude: depotLat,
            default_start_longitude: depotLng,
            timezone: this.element.querySelector('[name=timezone]').value.trim() || 'UTC',
        };

        const operatorEl = this.element.querySelector('[name=default_operator]');
        if (operatorEl && operatorEl.value) {
            payload.default_operator = parseInt(operatorEl.value);
        }

        this.saving = true;
        let response;
        if (this.isNew) {
            payload.group = app.activeGroup.id;
            response = await app.api.post('/api/routes/template', payload);
        } else {
            response = await app.api.post(`/api/routes/template/${this.template_model.get('id')}`, payload);
        }
        this.saving = false;

        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to save template');
            return;
        }

        const savedData = response.data.data;
        if (this.isNew) {
            this.isNew = false;
            this._templateId = savedData.id;
            await this._loadTemplate(savedData.id);
            app.toast.success('Template created');
        } else {
            this.template_model = new RouteTemplate(savedData);
            app.toast.success('Template saved');
        }
        this.render();
        this._refreshMap();
        this.emit('saved', savedData);
    }

    async onActionOptimise() {
        const id = this.template_model?.get('id');
        if (!id || this.optimising) return;

        const confirmed = await Dialog.confirm({
            title: 'Optimise Route',
            message: 'Re-sequence stops for the shortest total travel time. This will overwrite the current stop order.',
            confirmText: 'Optimise',
            confirmClass: 'btn-primary',
        });
        if (!confirmed) return;

        this.optimising = true;
        const optimiseBtn = this.element?.querySelector('[data-action="optimise"]');
        if (optimiseBtn) {
            optimiseBtn.disabled = true;
            optimiseBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Optimising…';
        }

        const response = await app.api.post(`/api/routes/template/${id}`, { action: 'optimize' });
        this.optimising = false;

        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Optimisation failed');
            this._refreshStopsPanel();
            return;
        }
        app.toast.success('Route optimised');
        await this._loadTemplate(id);
        this._refreshStopsPanel();
        this._refreshMap();
    }

    async onActionActivate() {
        const id = this.template_model?.get('id');
        if (!id) return;

        if (this.stops.length === 0) {
            app.toast.warning('Add at least one stop before activating');
            return;
        }

        const response = await app.api.post(`/api/routes/template/${id}`, { status: 'published' });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to activate template');
            return;
        }
        app.toast.success('Template activated');
        this.emit('activated', this.template_model);
    }

    async onActionAddStop() {
        if (this.isNew) {
            await this.onActionSave();
            if (this.isNew) return; // save failed (e.g. name missing)
        }
        this._openStopDrawer(null);
    }

    onActionEditStop(event, el) {
        const stopId = el.getAttribute('data-stop-id');
        const stop = this.stops.find(s => String(s.id) === stopId);
        if (!stop) return;
        this._openStopDrawer(stop);
    }

    async onActionRemoveStop(event, el) {
        const stopId = el.getAttribute('data-stop-id');
        const stop = this.stops.find(s => String(s.id) === stopId);
        if (!stop) return;

        const confirmed = await Dialog.confirm({
            title: 'Remove Stop',
            message: `Remove "${stop.label || stop.location?.name || 'this stop'}" from the route?`,
            confirmText: 'Remove',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await app.api.delete(`/api/routes/template_stop/${stopId}`);
        if (!response.success) {
            app.toast.error('Failed to remove stop');
            return;
        }
        app.toast.success('Stop removed');
        await this._loadStops();
        this._refreshStopsPanel();
        this._refreshMap();
    }

    _openStopDrawer(existingStop) {
        const drawer = new StopConfigDrawer({
            template: this.template_model,
            stop: existingStop || null,
            stopCount: this.stops.length,
        });
        const title = existingStop ? 'Edit Stop' : 'Add Stop';
        const buttonLabel = existingStop ? 'Update Stop' : 'Add Stop';
        Dialog.showDialog({
            title: `${title} — ${this.template_model.get('name')}`,
            body: drawer,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: buttonLabel, class: 'btn-primary',
                  handler: async () => {
                      const ok = await drawer.onActionSave();
                      if (ok) {
                          await this._loadStops();
                          this._refreshStopsPanel();
                          this._refreshMap();
                      }
                      return ok;
                  }
                },
            ],
        });
    }

    // ── Drag-to-reorder ───────────────────────────────────────────────────────

    _bindDrag() {
        const rows = this.element?.querySelectorAll('[data-drag-index]');
        if (!rows?.length) return;

        rows.forEach(row => {
            row.addEventListener('dragstart', e => {
                this._dragSrcIndex = parseInt(row.getAttribute('data-drag-index'));
                e.dataTransfer.effectAllowed = 'move';
            });
            row.addEventListener('dragover', e => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                rows.forEach(r => r.classList.remove('drag-over'));
                row.classList.add('drag-over');
            });
            row.addEventListener('dragleave', () => {
                row.classList.remove('drag-over');
            });
            row.addEventListener('drop', async e => {
                e.preventDefault();
                row.classList.remove('drag-over');
                const destIndex = parseInt(row.getAttribute('data-drag-index'));
                if (destIndex === this._dragSrcIndex) return;
                await this._reorderStop(this._dragSrcIndex, destIndex);
            });
        });
    }

    async _reorderStop(fromIndex, toIndex) {
        const stop = this.stops[fromIndex];
        if (!stop) return;

        // Optimistically reorder locally for instant feedback
        const reordered = [...this.stops];
        reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, stop);
        this.stops = reordered;
        this._refreshStopsPanel();
        this._refreshMap();

        // Persist to API
        const response = await app.api.post(`/api/routes/template_stop/${stop.id}`, {
            sequence_position: toIndex,
        });
        if (!response.success || !response.data.status) {
            app.toast.error('Failed to reorder stops');
        }
        // Refetch from server to confirm order
        await this._loadStops();
        this._refreshStopsPanel();
        this._refreshMap();
    }

    // ── Template rendering ────────────────────────────────────────────────────

    _templateSettingsHtml() {
        const t = this.template_model;
        const status = t?.get('status') || 'draft';
        const isPublished = status === 'published';

        const curOp = t?.get('default_operator');
        const curOpId = curOp && typeof curOp === 'object' ? curOp.id : (curOp || null);
        const operatorOptions = this.operators.map(op => {
            const selected = curOpId && String(curOpId) === String(op.id) ? 'selected' : '';
            return `<option value="${op.id}" ${selected}>${op.display_name || op.id}</option>`;
        }).join('');

        const lastOpt = t?.get('last_optimized_at');
        const distKm = t?.get('total_distance_meters') ? (t.get('total_distance_meters') / 1000).toFixed(1) : null;
        const durMins = t?.get('total_duration_seconds') ? Math.round(t.get('total_duration_seconds') / 60) : null;

        const statusBadge = {
            draft: '<span class="badge bg-secondary">Draft</span>',
            published: '<span class="badge bg-success">Published</span>',
            archived: '<span class="badge bg-warning text-dark">Archived</span>',
        }[status] || '';

        return `
            <div class="mb-4 d-flex align-items-center gap-2">
                <h6 class="mb-0">Settings</h6>
                ${statusBadge}
            </div>
            <div class="mb-3">
                <label class="form-label">Name *</label>
                <input type="text" class="form-control" name="name" value="${t?.get('name') || ''}" placeholder="e.g. North Loop">
            </div>
            <div class="mb-3">
                <label class="form-label">Type</label>
                <select class="form-select" name="route_type">
                    <option value="">— Select —</option>
                    ${['Stocking', 'Collection', 'Maintenance', 'Inspection', 'Delivery', 'Other'].map(v =>
                        `<option value="${v}" ${t?.get('route_type') === v ? 'selected' : ''}>${v}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="mb-3">
                <label class="form-label">Recurrence</label>
                <select class="form-select" name="recurrence_pattern">
                    <option value="">— Select —</option>
                    ${[
                        'Daily',
                        'Mon – Fri',
                        'Mon / Wed / Fri',
                        'Tue / Thu',
                        'Weekly',
                        'Fortnightly',
                        'Monthly',
                        'As needed',
                    ].map(v =>
                        `<option value="${v}" ${t?.get('recurrence_pattern') === v ? 'selected' : ''}>${v}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="mb-3">
                <label class="form-label">Start Address (Depot)</label>
                <input type="text" class="form-control" name="default_start_address"
                    value="${t?.get('default_start_address') || ''}"
                    placeholder="Start typing an address…"
                    autocomplete="off">
                <div class="list-group position-absolute z-3 w-100" style="max-width:280px;"
                    data-container="depot-suggestions"></div>
                <div class="row g-2 mt-1">
                    <div class="col-6">
                        <input type="text" class="form-control form-control-sm ${t?.get('default_start_latitude') ? '' : 'border-warning'}"
                            name="default_start_latitude"
                            value="${t?.get('default_start_latitude') || ''}"
                            placeholder="Latitude">
                    </div>
                    <div class="col-6">
                        <input type="text" class="form-control form-control-sm ${t?.get('default_start_longitude') ? '' : 'border-warning'}"
                            name="default_start_longitude"
                            value="${t?.get('default_start_longitude') || ''}"
                            placeholder="Longitude">
                    </div>
                </div>
                <div class="form-text ${t?.get('default_start_latitude') && t?.get('default_start_longitude') ? 'text-muted' : 'text-warning'}">
                    ${t?.get('default_start_latitude') && t?.get('default_start_longitude')
                        ? '<i class="bi bi-pin-map me-1"></i>Depot set'
                        : '<i class="bi bi-exclamation-triangle me-1"></i>No depot coords'}
                </div>
            </div>
            <div class="mb-3">
                <label class="form-label">Timezone</label>
                <select class="form-select" name="timezone">
                    ${[
                        ['UTC', 'UTC'],
                        ['America/New_York', 'Eastern (ET)'],
                        ['America/Chicago', 'Central (CT)'],
                        ['America/Denver', 'Mountain (MT)'],
                        ['America/Los_Angeles', 'Pacific (PT)'],
                        ['America/Phoenix', 'Arizona (no DST)'],
                        ['America/Anchorage', 'Alaska (AKT)'],
                        ['Pacific/Honolulu', 'Hawaii (HST)'],
                        ['Europe/London', 'London (GMT/BST)'],
                        ['Europe/Paris', 'Central Europe (CET)'],
                        ['Europe/Helsinki', 'Eastern Europe (EET)'],
                        ['Asia/Dubai', 'Dubai (GST)'],
                        ['Asia/Kolkata', 'India (IST)'],
                        ['Asia/Singapore', 'Singapore (SGT)'],
                        ['Asia/Tokyo', 'Tokyo (JST)'],
                        ['Australia/Sydney', 'Sydney (AEST)'],
                    ].map(([val, label]) =>
                        `<option value="${val}" ${(t?.get('timezone') || 'UTC') === val ? 'selected' : ''}>${label}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="mb-4">
                <label class="form-label">Default Operator</label>
                <select class="form-select" name="default_operator">
                    <option value="">None</option>
                    ${operatorOptions}
                </select>
            </div>
            <hr>
            <div class="mb-2">
                <h6 class="mb-3">Optimisation</h6>
                ${!this.isNew ? `
                    <button type="button" class="btn btn-outline-primary w-100 mb-2" data-action="optimise"
                        ${this.optimising ? 'disabled' : ''}>
                        ${this.optimising
                            ? '<span class="spinner-border spinner-border-sm me-1"></span>Optimising…'
                            : '<i class="bi bi-stars me-1"></i>Optimise Route'}
                    </button>
                    <div class="small text-muted">
                        ${lastOpt ? `Last optimised: ${new Date(lastOpt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Not yet optimised'}
                    </div>
                    ${(distKm || durMins) ? `
                        <div class="small text-muted mt-1">
                            Est. total:${distKm ? ` ${distKm} km` : ''}${durMins ? ` · ${durMins} min` : ''}
                        </div>
                    ` : ''}
                ` : '<div class="small text-muted">Save template first to enable optimisation.</div>'}
            </div>
        `;
    }

    _stopsColumnHtml() {
        return `
            <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 class="mb-0">Stops (${this.stops.length})</h6>
                <button type="button" class="btn btn-outline-primary btn-sm" data-action="add-stop">
                    <i class="bi bi-plus-lg me-1"></i>Add Stop
                </button>
            </div>
            ${this._stopsHtml()}
        `;
    }

    _refreshStopsPanel() {
        const panel = this.element?.querySelector('[data-container="stops-column"]');
        if (!panel) return;
        panel.innerHTML = this._stopsColumnHtml();
        this._bindDrag();
    }

    _stopsHtml() {
        const id = this.template_model?.get('id');

        if (this.stops.length === 0) {
            return `
                <div class="text-center text-muted py-5">
                    <i class="bi bi-geo-alt fs-2 d-block mb-2"></i>
                    No stops yet.
                    <div class="mt-2">
                        <button type="button" class="btn btn-primary btn-sm" data-action="add-stop">
                            <i class="bi bi-plus-lg me-1"></i>Add First Stop
                        </button>
                    </div>
                    ${this.isNew ? '<div class="small text-muted mt-2">Template will be saved automatically.</div>' : ''}
                </div>
            `;
        }

        const formatTravelTime = (secs) => {
            if (!secs) return null;
            const mins = Math.round(secs / 60);
            if (mins < 60) return `${mins}min`;
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return m > 0 ? `${h}h ${m}min` : `${h}h`;
        };

        const toHHMM = (t) => {
            if (!t) return null;
            const match = t.match(/(\d{2}:\d{2})/);
            return match ? match[1] : null;
        };

        const rows = this.stops.map((stop, index) => {
            const locName = stop.location?.name || stop.label || `Stop ${index + 1}`;
            const machineName = stop.machine?.name || '';
            const activity = stop.activity_type || '—';
            const dwell = stop.dwell_minutes ? `${stop.dwell_minutes}min` : '';
            const timeWindow = (stop.earliest_arrival && stop.latest_departure)
                ? `${stop.earliest_arrival} – ${stop.latest_departure}`
                : '';
            const priority = stop.priority && stop.priority !== 'normal'
                ? `<span class="badge bg-warning text-dark ms-1 small">${stop.priority}</span>`
                : '';

            const eta = toHHMM(stop.estimated_arrival);
            const travelTime = formatTravelTime(stop.travel_time_from_prev_seconds);
            const isBreached = eta && stop.earliest_arrival && stop.latest_departure
                ? (eta < stop.earliest_arrival.slice(0, 5) || eta > stop.latest_departure.slice(0, 5))
                : false;

            const etaHtml = eta ? `
                <div class="small mt-1 d-flex align-items-center gap-2">
                    ${travelTime && index > 0
                        ? `<span class="text-muted"><i class="bi bi-arrow-right-short"></i>${travelTime}</span>`
                        : ''}
                    <span class="${isBreached ? 'text-danger fw-semibold' : 'text-success'}">
                        <i class="bi bi-clock${isBreached ? '-fill' : ''} me-1"></i>ETA ${eta}
                        ${isBreached ? '<span class="badge bg-danger ms-1">Window breach</span>' : ''}
                    </span>
                </div>` : '';

            return `
                <div class="border-bottom d-flex align-items-start gap-2 px-2 py-2 stop-row"
                    draggable="true" data-drag-index="${index}">
                    <div class="text-muted pt-1 drag-handle" style="cursor:grab;">
                        <i class="bi bi-grip-vertical"></i>
                    </div>
                    <div class="text-muted small pt-1 fw-semibold" style="min-width:1.4rem;">${index + 1}</div>
                    <div class="flex-grow-1 min-w-0">
                        <div class="fw-semibold small">${locName}${priority}</div>
                        <div class="text-muted small">
                            ${machineName ? `${machineName} · ` : ''}${activity}${dwell ? ` · ${dwell}` : ''}
                        </div>
                        ${timeWindow ? `<div class="text-muted small">${timeWindow}</div>` : ''}
                        ${etaHtml}
                    </div>
                    <div class="dropdown">
                        <button type="button" class="btn btn-sm btn-link text-muted p-0"
                            data-bs-toggle="dropdown" aria-expanded="false">
                            <i class="bi bi-three-dots-vertical"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                            <li><a class="dropdown-item" href="#" data-action="edit-stop" data-stop-id="${stop.id}">Edit</a></li>
                            <li><a class="dropdown-item text-danger" href="#" data-action="remove-stop" data-stop-id="${stop.id}">Remove</a></li>
                        </ul>
                    </div>
                </div>
            `;
        }).join('');

        const breachCount = this.stops.filter(s => {
            const eta = toHHMM(s.estimated_arrival);
            return eta && s.earliest_arrival && s.latest_departure
                ? (eta < s.earliest_arrival.slice(0, 5) || eta > s.latest_departure.slice(0, 5))
                : false;
        }).length;

        const breachBanner = breachCount > 0 ? `
            <div class="alert alert-danger py-2 small mb-3">
                <i class="bi bi-exclamation-triangle-fill me-1"></i>
                <strong>${breachCount} stop${breachCount !== 1 ? 's' : ''} breach ${breachCount !== 1 ? 'their' : 'its'} time window</strong>
                — adjust dwell times or time windows, then re-optimise.
            </div>` : '';

        return `
            ${breachBanner}
            <div class="border rounded overflow-hidden">
                ${rows}
                <div class="p-2 text-center border-top">
                    <button type="button" class="btn btn-outline-secondary btn-sm" data-action="add-stop">
                        <i class="bi bi-plus-lg me-1"></i>Add Stop
                    </button>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }

    get template() {
        const t = this.template_model;
        const name = t?.get('name') || (this.isNew ? 'New Template' : '');
        const id = t?.get('id');
        const status = t?.get('status') || 'draft';
        const isDraft = status === 'draft';

        return `
            <div class="d-flex flex-column" style="min-height:0;">
                <div class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom flex-wrap gap-2">
                    <h5 class="mb-0">${name || 'New Template'}</h5>
                    <div class="d-flex gap-2">
                        ${id && isDraft ? `
                            <button type="button" class="btn btn-success btn-sm" data-action="activate">
                                <i class="bi bi-check-circle me-1"></i>Activate
                            </button>
                        ` : ''}
                        <button type="button" class="btn btn-primary btn-sm" data-action="save">
                            <i class="bi bi-floppy me-1"></i>Save
                        </button>
                    </div>
                </div>

                <div class="d-flex flex-grow-1" style="min-height:560px; overflow:hidden;">
                    <!-- Settings column -->
                    <div class="border-end p-3" style="width:280px; min-width:240px; overflow-y:auto;">
                        ${this._templateSettingsHtml()}
                    </div>

                    <!-- Stops column -->
                    <div class="border-end p-3 flex-shrink-0" style="width:300px; min-width:260px; overflow-y:auto;" data-container="stops-column">
                        ${this._stopsColumnHtml()}
                    </div>

                    <!-- Map column -->
                    <div class="flex-grow-1" style="min-width:0;">
                        <div data-container="planning-map" style="height:100%;"></div>
                    </div>
                </div>
            </div>
        `;
    }

    _onModelChange() {
        // suppress automatic re-render on model change
    }
}

// ── Planning map panel ────────────────────────────────────────────────────────

class TemplatePlanningMap extends View {
    constructor({ stops = [], depot = null, containerId } = {}) {
        super({ containerId });
        this.stops = stops;
        this.depot = depot;
        this.mapView = null;
        this._mapReady = false;
        this._routeCoords = null;
    }

    async onInit() {
        try {
            const { MapLibreView } = await import('web-mojo/map');
            const stopsWithCoords = this._stopsWithCoords();
            this._routeCoords = this._buildRouteCoords(stopsWithCoords);

            this.mapView = new MapLibreView({
                containerId: 'map',
                markers: this._buildMarkers(stopsWithCoords),
                height: 560,
                style: 'streets',
            });
            this.addChild(this.mapView);
            this._mapReady = true;
        } catch (e) {
            // map not available
        }
    }

    async onAfterRender() {
        await super.onAfterRender();
        this._drawRouteLine();
    }

    updateStops(stops, depot = null) {
        this.stops = stops;
        if (depot !== undefined) this.depot = depot;

        if (!this._mapReady || !this.mapView) return;

        const stopsWithCoords = this._stopsWithCoords();
        this._routeCoords = this._buildRouteCoords(stopsWithCoords);
        this.mapView.updateMarkers(this._buildMarkers(stopsWithCoords));
        this._redrawRouteLine();
    }

    updateDepot(depot) {
        this.depot = depot;
        if (!this._mapReady || !this.mapView) return;

        const stopsWithCoords = this._stopsWithCoords();
        this.mapView.updateMarkers(this._buildMarkers(stopsWithCoords));
    }

    _stopsWithCoords() {
        return this.stops.filter(s =>
            (s.location?.latitude && s.location?.longitude) ||
            (s.latitude && s.longitude)
        );
    }

    _buildMarkers(stopsWithCoords) {
        const toHHMM = (t) => { const m = t?.match(/(\d{2}:\d{2})/); return m ? m[1] : null; };
        const markers = [];

        if (this.depot?.lat && this.depot?.lng) {
            markers.push({
                lng: this.depot.lng,
                lat: this.depot.lat,
                color: '#16a34a',
                popup: `<strong>Depot</strong>${this.depot.address ? `<br>${this.depot.address}` : ''}`,
            });
        }

        stopsWithCoords.forEach((stop, i) => {
            const lat = stop.location?.latitude || stop.latitude;
            const lng = stop.location?.longitude || stop.longitude;
            const label = stop.label || stop.location?.name || `Stop ${i + 1}`;
            const activity = stop.activity_type || '';
            const eta = toHHMM(stop.estimated_arrival);
            const isBreach = eta && stop.earliest_arrival && stop.latest_departure
                ? (eta < toHHMM(stop.earliest_arrival) || eta > toHHMM(stop.latest_departure))
                : false;
            markers.push({
                lng,
                lat,
                color: isBreach ? '#dc3545' : '#3b82f6',
                popup: `<strong>${i + 1}. ${label}</strong>${activity ? `<br>${activity}` : ''}${eta ? ` · ETA ${eta}` : ''}`,
            });
        });

        return markers;
    }

    _buildRouteCoords(stopsWithCoords) {
        const coords = [];
        if (this.depot?.lat && this.depot?.lng) {
            coords.push([this.depot.lng, this.depot.lat]);
        }
        stopsWithCoords.forEach(s => {
            coords.push([
                s.location?.longitude || s.longitude,
                s.location?.latitude || s.latitude,
            ]);
        });
        return coords;
    }

    _drawRouteLine() {
        if (!this.mapView || !this._routeCoords || this._routeCoords.length < 2) return;
        const map = this.mapView.map;
        if (!map) return;

        const addLine = () => {
            if (map.getSource('route-path')) return;
            map.addSource('route-path', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: this._routeCoords },
                },
            });
            map.addLayer({
                id: 'route-path-line',
                type: 'line',
                source: 'route-path',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': '#3b82f6',
                    'line-width': 3,
                    'line-opacity': 0.7,
                    'line-dasharray': [2, 1],
                },
            });
        };

        if (map.loaded()) {
            addLine();
        } else {
            map.once('load', addLine);
        }
    }

    _redrawRouteLine() {
        const map = this.mapView?.map;
        if (!map) return;

        const geojson = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: this._routeCoords || [] },
        };

        if (map.getSource('route-path')) {
            // Update in-place — no remove/re-add, works during camera animations
            map.getSource('route-path').setData(geojson);
        } else if (this._routeCoords?.length >= 2) {
            const addLine = () => {
                if (map.getSource('route-path')) return;
                map.addSource('route-path', { type: 'geojson', data: geojson });
                map.addLayer({
                    id: 'route-path-line',
                    type: 'line',
                    source: 'route-path',
                    layout: { 'line-join': 'round', 'line-cap': 'round' },
                    paint: {
                        'line-color': '#3b82f6',
                        'line-width': 3,
                        'line-opacity': 0.7,
                        'line-dasharray': [2, 1],
                    },
                });
            };
            if (map.loaded()) addLine();
            else map.once('load', addLine);
        }
    }

    set template(v) { this._t = v; }

    get template() {
        return `<div data-container="map" style="height:100%; min-height:560px;"></div>`;
    }
}
